import { Component, DoCheck, ElementRef, HostBinding, Input, OnDestroy, Optional, Self } from '@angular/core';
import { ControlValueAccessor, NgControl } from '@angular/forms';
import { MatFormFieldControl } from '@angular/material';
import { FocusMonitor } from '@angular/cdk/a11y';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { Subject } from 'rxjs';

import { BaseImageModel, FormImageModel, TrailerImageModel } from '@core/api';

import { UploadService } from './upload.service';

@Component({
  selector: 'ui-input-images',
  templateUrl: './input-images.component.html',
  styleUrls: ['./input-images.component.scss'],
  providers: [
    {
      provide: MatFormFieldControl,
      useExisting: InputImagesComponent
    }
  ]
})
export class InputImagesComponent implements DoCheck, OnDestroy, MatFormFieldControl<BaseImageModel[]>, ControlValueAccessor {
  static nextId = 0;

  private _placeholder: string;
  private _required = false;
  private _disabled = false;

  @HostBinding() id = `ui-input-images-${ InputImagesComponent.nextId++ }`;
  @HostBinding('attr.aria-describedby') describedBy = '';

  focused = false;
  errorState = false;
  controlType = 'ui-input-images';

  stateChanges = new Subject<void>();

  onChange: any = () => {
  };
  onTouched: any = () => {
  };

  @Input() length = 3;
  @Input() accept = 'image/*';

  @Input()
  get placeholder() {
    return this._placeholder;
  }

  set placeholder(plh) {
    this._placeholder = plh;
    this.stateChanges.next();
  }

  @Input()
  get required() {
    return this._required;
  }

  set required(req) {
    this._required = coerceBooleanProperty(req);
    this.stateChanges.next();
  }

  @Input()
  get disabled() {
    return this._disabled;
  }

  set disabled(dis) {
    this._disabled = coerceBooleanProperty(dis);
    this.stateChanges.next();
  }

  @Input()
  set value(value: BaseImageModel[]) {
    this.uploadService.images = value || [];
    this.stateChanges.next();
  }

  get value(): BaseImageModel[] {
    return this.uploadService.images;
  }

  @HostBinding('class.floating')
  get shouldLabelFloat() {
    return this.focused || !this.empty;
  }

  get empty() {
    return !this.value;
  }

  constructor(
    private uploadService: UploadService,
    private fm: FocusMonitor,
    private elRef: ElementRef<HTMLElement>,
    @Optional() @Self() public ngControl: NgControl
  ) {

    // Replace the provider from above with this.
    if (this.ngControl != null) {
      // Setting the value accessor directly (instead of using
      // the providers) to avoid running into a circular import.
      this.ngControl.valueAccessor = this;
    }

    fm.monitor(elRef.nativeElement, true).subscribe(origin => {
      this.focused = !!origin;
      this.stateChanges.next();
    });
  }

  onBlur() {
    this.ngControl.control.setValue(this.ngControl.control.value);
  }

  setDescribedByIds(ids: string[]) {
    this.describedBy = ids.join(' ');
  }

  setMain(key: number) {
    this.value.forEach(image => {
      image.main = 0;
    });

    this.value[key].main = 1;
  }

  remove(image: TrailerImageModel, btn: HTMLButtonElement) {
    if (image.id) {
      this.uploadService.removeImageIds.push(image.id);
    }

    const index = this.value.findIndex(img => img.id === image.id);

    this.value.splice(index, 1);
  }

  changeNewFile(event) {
    const fileReader = new FileReader();

    fileReader.onload = () => {
      this.value.push({
        description: '',
        main: 0,
        name: '',
        original_name: event.target.files.item(0).name,
        url: fileReader.result as string,
        file: event.target.files.item(0)
      });

      this.onChange(this.value);
    };

    fileReader.readAsDataURL(event.target.files[0]);
  }

  onContainerClick(event: MouseEvent) {
    if ((event.target as Element).tagName.toLowerCase() != 'input') {
      this.elRef.nativeElement.querySelector('input').focus();
    }
  }

  writeValue(value: FormImageModel[]): void {
    this.value = value;

    this.onChange(value);
    this.onTouched();
  }

  registerOnChange(fn) {
    this.onChange = fn;
  }

  registerOnTouched(fn) {
    this.onTouched = fn;
  }

  ngDoCheck(): void {
    if (this.ngControl) {
      this.errorState = this.ngControl.invalid && this.ngControl.touched;

      this.stateChanges.next();
    }
  }

  ngOnDestroy() {
    this.stateChanges.complete();
    this.fm.stopMonitoring(this.elRef.nativeElement);
  }

}
